import { AgGridTable } from '@shared/components/ui/AgGridTable';
import {
  parseCourierSettlementChargeLine,
  type CourierSettlementChargeLine,
} from '../../../models/courierSettlementChargeLine';
import { useSettlementChargeLinesAgGridColumns } from '../../hooks/useSettlementChargeLinesAgGridColumns';
import { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface SettlementChargeLinesTableProps {
  rows: unknown[];
  fetchNextPage?: () => void | Promise<unknown>;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  /** Refetch / primera carga en segundo plano (misma convención que Orders List). */
  isFetching?: boolean;
  /** Hay filtros o búsqueda aplicados (mensaje vacío). */
  hasActiveFilters?: boolean;
}

export function SettlementChargeLinesTable({
  rows,
  fetchNextPage,
  hasNextPage = false,
  isFetchingNextPage = false,
  isFetching = false,
  hasActiveFilters = false,
}: SettlementChargeLinesTableProps) {
  const { t } = useTranslation('courierInvoicesModule');
  const { columnDefs } = useSettlementChargeLinesAgGridColumns();

  const lines = useMemo(() => {
    const out: CourierSettlementChargeLine[] = [];
    for (const raw of rows) {
      const parsed = parseCourierSettlementChargeLine(raw);
      if (parsed) out.push(parsed);
    }
    return out;
  }, [rows]);

  const emptyMessage = hasActiveFilters
    ? t('settlement.lines_filters.grid_empty_filtered')
    : t('settlement.empty_rows');

  return (
    <div className="space-y-3" data-testid="settlement-charge-lines-table">
      {!lines.length ? (
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      ) : (
        <div
          className={`w-full ${isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none transition-opacity duration-300' : ''}`}
          data-testid="settlement-charge-lines-scroll"
        >
          <AgGridTable
            data={lines}
            columnDefs={columnDefs as ColDef[]}
            height="calc(100vh - 200px)"
            enableSelection={false}
            isLoading={false}
            rowHeight={50}
            className="rounded-lg shadow-sm"
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            showLoadMoreButton={false}
            infiniteScrollThreshold={200}
            gridOptions={{
              suppressCellFocus: true,
            }}
          />
        </div>
      )}
    </div>
  );
}
